import { PHOTO_CONFIG, PHOTO_WEBHOOKS } from '../constants/photoConfig';
import { getIdTokenClaims, buildUserPayload } from '../auth/photoAuth';
import { extractCoins } from '../utils/coins';
import { extractS3ObjectKeyFromUrl } from '../utils/s3';
import { extractImageHistoryItems, normalizeHistoryItem, filterDeletedImages } from '../utils/history';

export async function fetchUserCoins({ reason = 'manual', user }) {
  // user_info 웹훅: 기본은 POST로 호출하되, 환경에 따라 GET만 허용할 수 있어 fallback 포함
  const { idToken, claims } = await getIdTokenClaims();
  const payload = buildUserPayload({ user, claims });

  const doFetch = async (method) => {
    const opts = {
      method,
      headers: {
        ...(method !== 'GET' ? { 'Content-Type': 'application/json' } : {}),
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
        'ngrok-skip-browser-warning': PHOTO_CONFIG.ngrokSkipBrowserWarning,
      },
    };
    if (method !== 'GET') {
      opts.body = JSON.stringify({
        reason,
        requestedAt: new Date().toISOString(),
        user: { username: user?.username, userId: user?.userId },
        payload,
      });
    }
    return fetch(PHOTO_WEBHOOKS.userInfo, opts);
  };

  let response = await doFetch('POST');
  if (!response.ok) response = await doFetch('GET');

  const raw = await response.text();
  let data;
  try {
    data = JSON.parse(raw);
  } catch {
    data = raw;
  }
  return extractCoins(data);
}

export async function notifyDeleteImageToWebhook({ deletedId, imageHistory, user }) {
  const item = Array.isArray(imageHistory) ? imageHistory.find((it) => String(it?.id) === String(deletedId)) : null;
  const url = item?.url || null;
  const s3Key = item?.s3Key || extractS3ObjectKeyFromUrl(url);

  const { idToken, claims } = await getIdTokenClaims();
  const payload = buildUserPayload({ user, claims });

  await fetch(PHOTO_WEBHOOKS.deleteImage, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      'ngrok-skip-browser-warning': PHOTO_CONFIG.ngrokSkipBrowserWarning,
    },
    body: JSON.stringify({
      reason: 'mypage_history_delete',
      requestedAt: new Date().toISOString(),
      imageId: String(deletedId),
      url,
      s3Key,
      payload,
    }),
  });
}

export async function fetchImageHistory({
  reason = 'mypage_open',
  user,
  deletedIds = [],
  retentionDays = PHOTO_CONFIG.historyRetentionDays,
  limit = 50,
}) {
  const { idToken, claims } = await getIdTokenClaims();
  const payload = buildUserPayload({ user, claims });

  const response = await fetch(PHOTO_WEBHOOKS.imageHistory, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      'ngrok-skip-browser-warning': PHOTO_CONFIG.ngrokSkipBrowserWarning,
    },
    body: JSON.stringify({
      reason,
      requestedAt: new Date().toISOString(),
      retentionDays,
      limit,
      payload,
    }),
  });

  const contentType = (response.headers.get('content-type') || '').toLowerCase();

  // 1) 이미지 바이너리 응답
  if (contentType.startsWith('image/')) {
    if (!response.ok) throw new Error(`get-image webhook error(image): ${response.status}`);
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const items = [
      {
        id: `${Date.now()}-0`,
        url: objectUrl,
        occasion: '',
        meme: '',
        customText: '',
        createdAt: new Date().toISOString(),
      },
    ];
    return filterDeletedImages(items, deletedIds);
  }

  // 2) JSON 응답
  if (contentType.includes('application/json')) {
    const data = await response.json();
    if (!response.ok) throw new Error(`get-image webhook error(json): ${response.status}`);
    const items = extractImageHistoryItems(data).map((it, idx) => normalizeHistoryItem(it, idx)).filter(Boolean);

    const cutoff = Date.now() - retentionDays * 24 * 60 * 60 * 1000;
    const filtered = items.filter((it) => {
      try {
        return new Date(it.createdAt).getTime() >= cutoff;
      } catch {
        return true;
      }
    });
    return filterDeletedImages(filtered, deletedIds);
  }

  // 3) 기타: text로 읽고 JSON 시도
  const rawText = await response.text();
  if (!response.ok) throw new Error(`get-image webhook error: ${response.status} ${String(rawText).slice(0, 120)}`);

  let data;
  try {
    data = JSON.parse(rawText);
  } catch {
    data = rawText;
  }

  const items = extractImageHistoryItems(data).map((it, idx) => normalizeHistoryItem(it, idx)).filter(Boolean);

  const cutoff = Date.now() - retentionDays * 24 * 60 * 60 * 1000;
  const filtered = items.filter((it) => {
    try {
      return new Date(it.createdAt).getTime() >= cutoff;
    } catch {
      return true;
    }
  });
  return filterDeletedImages(filtered, deletedIds);
}

export async function submitCustomerSupport({
  title,
  content,
  isGuest = false,
  user,
  userName,
  pageUrl = window.location.href,
}) {
  const t = String(title || '').trim();
  const c = String(content || '').trim();
  if (!t || !c) throw new Error('제목과 내용을 모두 입력해주세요.');

  const { claims } = await getIdTokenClaims();

  const payload = {
    title: t,
    content: c,
    pageUrl,
    eventTime: new Date().toISOString(),
    isGuest: !!isGuest,
    user: {
      cognitoSub: claims?.sub,
      email: claims?.email,
      name: claims?.name,
      phone: claims?.phone_number,
      userName: user?.username || claims?.['cognito:username'] || claims?.username || userName || null,
    },
    client: {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
    },
  };

  const res = await fetch(PHOTO_WEBHOOKS.customerSupport, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`photo-cs webhook error: ${res.status} ${String(body).slice(0, 200)}`);
  }
}


